import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";

import {
  CloseOutlined,
  LoadingOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button, Modal, Pagination, Spin,
} from "antd";
import moment from "moment";
import { toast } from "react-toastify";

import NoResultsGif from "../../../../Common/NoResultsGIF";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import SwabListExport from "../SwabList/SwabListExport";
import SwabListTable from "../SwabList/SwabListTable";

import { analyticsSampleDataFetch } from "../../../../../actions/envAnalytics";
import { normalizeVolumeTrendSampleRows } from "../../utils";

import pumpkinGif from "../../../../../assets/images/environment/pumpkin.gif";
import "./SwabListModal.css";

const TABLE_HEADERS = ["Sample ID", "Sample Type"];
const TESTS_COLUMN_LABEL = "Tests";
const FINAL_RESULT_COLUMN_LABEL = "Final Result";

const formatCount = (value) => String(value).padStart(2, "0");

const TestCountChip = ({
  testName, count, isSelected, onClick,
}) => (
  <button
    type="button"
    className={`SwabListModal__TestChip ${
      isSelected ? "SwabListModal__TestChip--active" : "SwabListModal__TestChip--inactive"
    }`}
    aria-pressed={isSelected}
    onClick={() => onClick(testName)}
  >
    {testName}
    {" "}
    (
    {formatCount(count)}
    )
  </button>
);

const splitTests = (tests) => (tests || "")
  .split(",")
  .map((test) => test.trim())
  .filter(Boolean);

export default function SwabListModal({
  open,
  onClose,
  date,
}) {
  const [exportOpen, setExportOpen] = useState(false);
  const [swabList, setSwabList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilterTests, setSelectedFilterTests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const abortControllerRef = useRef(null);

  const formattedDate = date ? moment(date).format("MMM D, YYYY") : "-";
  const modalTitle = `Test Volume trend on ${formattedDate}`;

  const summaryChips = useMemo(() => {
    const counts = {};
    swabList.forEach((row) => {
      splitTests(row.tests).forEach((testName) => {
        counts[testName] = (counts[testName] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([testName, count]) => ({ testName, count }));
  }, [swabList]);

  const allChipTests = useMemo(
    () => summaryChips.map((chip) => chip.testName),
    [summaryChips],
  );

  useEffect(() => {
    if (!open) return;
    setSelectedFilterTests(allChipTests);
  }, [open, allChipTests]);

  const handleToggleTest = useCallback((testName) => {
    setSelectedFilterTests((prev) => (
      prev.includes(testName)
        ? prev.filter((name) => name !== testName)
        : [...prev, testName]
    ));
  }, []);

  const filteredRows = useMemo(() => {
    if (selectedFilterTests.length === 0) return [];
    const selectedSet = new Set(selectedFilterTests);
    return swabList.filter((row) => splitTests(row.tests)
      .some((test) => selectedSet.has(test)));
  }, [swabList, selectedFilterTests]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredRows]);

  useEffect(() => {
    if (!open || !date) {
      return undefined;
    }

    const fetchSwabs = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setIsLoading(true);

      try {
        const dateStr = moment(date).format("YYYY-MM-DD");
        const response = await analyticsSampleDataFetch(
          {
            date_from: dateStr,
            date_to: dateStr,
          },
          controller.signal,
        );

        if (controller.signal.aborted) return;

        if (response.success) {
          setSwabList(normalizeVolumeTrendSampleRows(response.result, "samples_received"));
        } else if (!response.canceled) {
          setSwabList([]);
          toast.error("Failed to load swab list");
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          toast.error("Error loading swab list");
          setSwabList([]);
        }
      } finally {
        if (abortControllerRef.current === controller) {
          setIsLoading(false);
        }
      }
    };

    fetchSwabs();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [open, date]);

  useEffect(() => {
    if (!open) {
      setSwabList([]);
      setExportOpen(false);
      setIsLoading(false);
      setSelectedFilterTests([]);
    }
  }, [open]);

  const handleExport = () => {
    setExportOpen(true);
  };

  const handleCloseExport = () => {
    setExportOpen(false);
  };

  return (
    <>
      <Modal
        open={open}
        closable={false}
        centered
        footer={null}
        onCancel={onClose}
        className="SwabListModal SwabListModal--volumeTrend"
        width="960px"
        destroyOnHidden
      >
        <div className="SwabListModal__Header">
          <span className="SwabListModal__HeaderTitle">{modalTitle}</span>
          <CloseOutlined
            className="SwabListModal__CloseIcon"
            aria-label="Close"
            onClick={onClose}
          />
        </div>
        <div className="SwabListModal__Toolbar">
          <div className="SwabListModal__ChipsScroll">
            <div className="SwabListModal__TestChips">
              {summaryChips.map(({ testName, count }) => (
                <TestCountChip
                  key={testName}
                  testName={testName}
                  count={count}
                  isSelected={selectedFilterTests.includes(testName)}
                  onClick={handleToggleTest}
                />
              ))}
            </div>
          </div>
          <Button
            type="default"
            className="SwabListModal__ExportButton"
            icon={<UploadOutlined />}
            onClick={handleExport}
            disabled={isLoading || filteredRows.length === 0}
          >
            Export
          </Button>
        </div>
        <div className="SwabListModal__Content SwabListModal__Content--volumeTrend">
          <StyledScrollbar className="SwabListModal__ContentScrollbar">
            {isLoading ? (
              <div className="SwabListModal__Loading">
                <Spin indicator={<LoadingOutlined spin />} />
              </div>
            ) : filteredRows.length > 0 ? (
              <SwabListTable
                data={paginatedRows}
                includeReceivedDate={false}
                includeFinalResult
                scrollY={false}
              />
            ) : (
              <NoResultsGif message="No Data" image={pumpkinGif} className="SwabListModal__NoResults" />
            )}
          </StyledScrollbar>
          {!isLoading && filteredRows.length > 0 && (
            <Pagination
              className="SwabListModal__Pagination"
              current={currentPage}
              pageSize={pageSize}
              total={filteredRows.length}
              size="small"
              showSizeChanger
              pageSizeOptions={[10]}
              hideOnSinglePage
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
            />
          )}
        </div>
      </Modal>
      <SwabListExport
        open={exportOpen}
        onClose={handleCloseExport}
        swabList={filteredRows}
        tableHeaders={TABLE_HEADERS}
        testsColumnLabel={TESTS_COLUMN_LABEL}
        finalResultColumnLabel={FINAL_RESULT_COLUMN_LABEL}
        modalTitle={modalTitle}
        dateRange={formattedDate}
        includeReceivedDate={false}
      />
    </>
  );
}
